import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { Alert, Button, Card, Heading, Input } from '@aaimplatform/ui';
import { useAcceptInvitation } from '../hooks/useStaffLifecycle';

const MIN_PASSWORD_LENGTH = 12;

export function AcceptInvitationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const acceptInvitation = useAcceptInvitation();

  const plaintextToken = searchParams.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!plaintextToken) {
      setValidationError('No invitation token in URL. Use the link from your invitation email.');
    }
  }, [plaintextToken]);

  const apiError =
    acceptInvitation.error instanceof Error ? acceptInvitation.error.message : null;
  const displayError = validationError ?? apiError;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (!plaintextToken) {
      setValidationError('Missing invitation token');
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setValidationError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (password !== confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    acceptInvitation.mutate(
      { plaintextToken, password },
      {
        onSuccess: (response) => {
          if (response.credentialPathEnabled && response.identityRedirectUrl) {
            const url = new URL(response.identityRedirectUrl);
            navigate(`/login?invitationId=${url.searchParams.get('invitationId') ?? ''}`);
            return;
          }
          navigate('/login');
        },
      },
    );
  };

  if (acceptInvitation.isSuccess && !acceptInvitation.data.credentialPathEnabled) {
    return (
      <div className="min-h-screen bg-canvas flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="overflow-hidden">
            <div className="h-1 bg-accent" />
            <div className="p-8">
              <Heading level={1} size="page">
                Invitation Accepted
              </Heading>
              <p className="mt-4 text-secondary">
                Your institution uses single sign-on. You can sign in with your organization
                identity provider.
              </p>
              <Button
                className="mt-6 w-full"
                onClick={() => navigate('/login')}
              >
                Go to sign in
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="overflow-hidden">
          <div className="h-1 bg-accent" />
          <div className="p-8">
            <div className="flex justify-center mb-6">
              <Lock className="w-10 h-10 text-accent" />
            </div>
            <Heading level={1} size="page" className="text-center">
              Set your password
            </Heading>
            <p className="mt-2 text-center text-secondary text-sm">
              Choose a password to complete your Aaim Platform invitation. You will be
              asked to set up an authenticator app on your first sign-in.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-secondary">
                  Password
                </label>
                <div className="mt-1 relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={`Minimum ${MIN_PASSWORD_LENGTH} characters`}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-tertiary"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-secondary">
                  Confirm password
                </label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  className="mt-1"
                />
              </div>

              {displayError && (
                <Alert type="error">
                  {displayError}
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={acceptInvitation.isPending || !plaintextToken}
              >
                {acceptInvitation.isPending ? 'Accepting...' : 'Set password and continue'}
              </Button>
            </form>
          </div>
        </Card>
      </div>
    </div>
  );
}
